class LocalizationSelectors extends HTMLElement {
  connectedCallback() {
    this.cacheElements();
    this.bindEvents();
    this.updateSubmitVisibility();
  }

  cacheElements() {
    this.form = this.querySelector('form');
    this.countryInput = this.querySelector('[data-input-type="country"]');
    this.languageInput = this.querySelector('[data-input-type="language"]');
    this.countrySelect = this.querySelector('[data-shopify-select="country"]');
    this.languageSelect = this.querySelector('[data-shopify-select="language"]');
    this.submitButton = this.querySelector('[data-localization-submit]');
    this.languageSelectorContainer = this.querySelector('[data-language-selector-container]');
    this.englishOnlyTemplate = this.querySelector('[data-english-only-template]');

    this.isUsaSelected = false;
    this.usaRedirectUrl = null;

    this.languageSelectorCache = new Map();
    
    this.initialValues = {
      country: this.countryInput?.value || '',
      language: this.languageInput?.value || ''
    };
  }

  bindEvents() {
    this.addEventListener('click', e => {
      const selectorToggle = e.target.closest('[data-selector-type]');
      const option = e.target.closest('[data-value]');
      
      if (selectorToggle) {
        e.preventDefault();
        this.toggleDropdown(selectorToggle);
      } else if (option) {
        e.preventDefault();
        this.selectOption(option);
      }
    });

    document.addEventListener('click', e => {
      if (!e.target.closest('[data-selector-type]')) {
        this.closeDropdowns();
      }
    });

    this.submitButton?.addEventListener('click', e => {
      e.preventDefault();
      if (this.isUsaSelected && this.usaRedirectUrl) {
        window.location.href = this.usaRedirectUrl;
      } else {
        this.form?.submit();
      }
    });
  }

  toggleDropdown(selector) {
    const dropdown = selector.nextElementSibling;
    const isOpen = dropdown?.hasAttribute('data-open');

    this.closeDropdowns();

    if (!isOpen && dropdown) {
      dropdown.setAttribute('data-open', '');
      selector.setAttribute('data-open', '');
    }
  }

  closeDropdowns() {
    this.querySelectorAll('[data-open]').forEach(el => el.removeAttribute('data-open'));
  }

  selectOption(option) {
    const container = option.closest('[data-dropdown-type]').previousElementSibling;
    const type = container.dataset.selectorType;
    const value = option.dataset.value;

    this.updateSelection(option);
    this.updateUI(container, option, type);
    this.updateInputs(type, value);
    this.closeDropdowns();

    if (type === 'country') {
      if (option.dataset.usStore === 'true') {
        this.isUsaSelected = true;
        this.usaRedirectUrl = option.dataset.redirectUrl;
        this.showEnglishOnlyLanguage();
      } else {
        this.isUsaSelected = false;
        this.usaRedirectUrl = null;
        this.rebuildLanguageSelector(value);
      }
    }

    this.updateSubmitVisibility();
  }

  updateSelection(selectedOption) {
    const container = selectedOption.closest('[data-dropdown-type]');
    container.querySelectorAll('[data-selected]').forEach(opt => opt.removeAttribute('data-selected'));
    selectedOption.setAttribute('data-selected', '');
  }

  updateUI(current, option, type) {
    const currentImage = current.querySelector('img');
    const optionImage = option.querySelector('img');
    const currentText = current.querySelector('[data-text]');

    currentImage?.remove();
    
    if (optionImage) {
      const newImage = optionImage.cloneNode(true);
      current.insertBefore(newImage, currentText);
    }

    if (currentText) {
      currentText.textContent = type === 'country' 
        ? `${option.dataset.countryName} (${option.dataset.currencyCode} ${option.dataset.currencySymbol})`
        : option.dataset.languageName;
    }
  }

  updateInputs(type, value) {
    const inputs = {
      country: [this.countryInput, this.countrySelect],
      language: [this.languageInput, this.languageSelect]
    };

    inputs[type]?.forEach(input => input && (input.value = value));
  }

  updateSubmitVisibility() {
    const currentValues = {
      country: this.countryInput?.value || '',
      language: this.languageInput?.value || ''
    };

    const hasChanges = Object.keys(currentValues).some(key => 
      currentValues[key] !== this.initialValues[key]
    );

    if (this.submitButton) {
      this.submitButton.style.display = hasChanges ? "block" : "none";
      if (hasChanges) {
        const btn = this.submitButton;
        const blurIfFocused = () => {
          if (document.activeElement === btn) btn.blur();
        };
        requestAnimationFrame(blurIfFocused);
        setTimeout(blurIfFocused, 0);
      }
    }
  }

  showEnglishOnlyLanguage() {
    if (!this.languageSelectorContainer || !this.englishOnlyTemplate) return;

    const dropdown = this.languageSelectorContainer.querySelector('[data-dropdown-type="language"]');
    if (dropdown) {
      const ul = dropdown.querySelector('.localization-selector__options');
      if (ul) {
        ul.innerHTML = '';
        ul.appendChild(this.englishOnlyTemplate.content.cloneNode(true));
      }
    }

    const templateItem = this.englishOnlyTemplate.content.querySelector('[data-language-name]');
    const englishName = templateItem?.dataset.languageName || 'English';

    const languageCurrent = this.languageSelectorContainer.querySelector('[data-selector-type="language"]');
    if (languageCurrent) {
      languageCurrent.querySelector('img')?.remove();
      const currentText = languageCurrent.querySelector('[data-text]');
      if (currentText) currentText.textContent = englishName;
    }

    this.updateInputs('language', 'en');
    this.languageSelectorContainer.classList.remove('is-loading');
  }

  async rebuildLanguageSelector(countryCode) {
    if (!this.languageSelectorContainer) return;

    this.languageSelectorContainer.classList.add('is-loading');

    try {
      let languageSelectorData;

      if (this.languageSelectorCache.has(countryCode)) {
        languageSelectorData = this.languageSelectorCache.get(countryCode);
      } else {
        const url = new URL(window.location.href);
        url.searchParams.set('country', countryCode);
        
        const response = await fetch(url.toString());
        const html = await response.text();
        
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        const newLanguageSelector = doc.querySelector('[data-language-selector-container]');
        
        if (newLanguageSelector) {
          languageSelectorData = {
            innerHTML: newLanguageSelector.innerHTML,
            className: newLanguageSelector.className
          };
          
          this.languageSelectorCache.set(countryCode, languageSelectorData);
        }
      }

      if (languageSelectorData) {
        const cleanContainer = this.languageSelectorContainer.cloneNode(false);
        cleanContainer.innerHTML = languageSelectorData.innerHTML;
        cleanContainer.className = languageSelectorData.className;
        cleanContainer.setAttribute('data-language-selector-container', '');
        
        this.languageSelectorContainer.replaceWith(cleanContainer);
        this.languageSelectorContainer = cleanContainer;
        
        this.languageInput = this.querySelector('[data-input-type="language"]');
        this.languageSelect = this.querySelector('[data-shopify-select="language"]');
        
        this.updateSubmitVisibility();
      }
    } catch (error) {
      console.error('Failed to rebuild language selector:', error);
    } finally {
      this.languageSelectorContainer.classList.remove('is-loading');
    }
  }
}

class LocalizationDrawer extends HTMLElement {
  connectedCallback() {
    this.bindEvents();
  }

  bindEvents() {
    const desktopToggle = document.getElementById('localization-drawer-toggle');
    desktopToggle?.addEventListener('click', e => {
      e.preventDefault();
      this.open();
    });

    document.querySelectorAll('[data-localization-drawer-toggle]').forEach(toggle => {
      toggle.addEventListener('click', e => {
        e.preventDefault();
        this.open();
      });
    });

    this.querySelectorAll('[data-close], .side-panel-close').forEach(button => {
      button.addEventListener('click', () => this.close());
    });

    const clickCapture = document.querySelector('.click-capture, [data-click-capture]');
    if (clickCapture) {
      clickCapture.addEventListener('click', () => {
        if (this.classList.contains('active')) {
          this.close();
        }
      });
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.classList.contains('active')) {
        this.close();
      }
    });
  }

  open() {
    document.body.classList.add('open-cc');
    this.classList.add('active');
    this.focus();
  }

  close() {
    if (!this.classList.contains('active')) return;
    
    const localizationSelectors = this.querySelector('localization-selectors');
    if (localizationSelectors) {
      localizationSelectors.closeDropdowns();
    }
    
    this.classList.remove('active');

    setTimeout(() => {
      if (!document.querySelector('.side-panel.active')) {
        document.body.classList.remove('open-cc');
      }
    }, 10);
  }
}

customElements.define('localization-selectors', LocalizationSelectors);
customElements.define('localization-drawer', LocalizationDrawer);
